<script setup>
/**
 * Global bottom-left toast. Driven entirely by `globalStore.toastNotification`;
 * prefer `globalStore.showToast(...)` / `dismissToast()` over poking the fields
 * directly (the actions own the auto-dismiss timer).
 *
 * Types: success · error · warning · progress · completed.
 * `progress` renders a spinner and never auto-dismisses — the caller replaces it
 * with `completed` or `error` when the queued job reports back (CDP-2459).
 */
import { computed } from 'vue'
import { useGlobalStore } from '@/stores/GlobalStore'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { TOAST_TYPES } from '@/constants'

const globalStore = useGlobalStore()

const toast = computed(() => globalStore.toastNotification)

// Per-type presentation. One lookup so a new type is one entry rather than a new
// ternary in three places.
const TYPE_STYLES = {
  [TOAST_TYPES.SUCCESS]: {
    icon: 'heroicons:check-circle',
    iconClass: 'text-green-500',
    messageClass: 'text-gray-600',
    surfaceClass: 'bg-white border',
  },
  [TOAST_TYPES.COMPLETED]: {
    icon: 'heroicons:check-circle',
    iconClass: 'text-tint-1',
    messageClass: 'text-tint-1 font-medium',
    surfaceClass: 'bg-white border',
  },
  [TOAST_TYPES.ERROR]: {
    icon: 'heroicons:exclamation-circle',
    iconClass: 'text-red-500',
    messageClass: 'text-red-600',
    surfaceClass: 'bg-red-50 border border-red-500',
  },
  [TOAST_TYPES.WARNING]: {
    icon: 'heroicons:exclamation-triangle',
    iconClass: 'text-yellow-500',
    messageClass: 'text-gray-600',
    surfaceClass: 'bg-yellow-50 border border-yellow-400',
  },
  [TOAST_TYPES.PROGRESS]: {
    icon: '',
    iconClass: '',
    messageClass: 'text-gray-500',
    surfaceClass: 'bg-white border',
  },
}

const style = computed(() => TYPE_STYLES[toast.value.type] ?? TYPE_STYLES[TOAST_TYPES.SUCCESS])
const isProgress = computed(() => toast.value.type === TOAST_TYPES.PROGRESS)

// Optional action button (e.g. "Refresh page"). Driven by
// `toastNotification.action = { label, icon, type, onClick }`. `type: 'reload'`
// reloads the page; an `onClick` callback runs (e.g. open a modal); otherwise the
// action just dismisses the toast.
const handleAction = () => {
  const action = toast.value.action
  globalStore.dismissToast()
  if (action?.type === 'reload') window.location.reload()
  else action?.onClick?.()
}
</script>

<template>
  <Transition name="fade">
    <section
      v-if="toast.show"
      class="fixed bottom-4 left-20 z-[1099] w-[372px] rounded-lg p-4 text-sm shadow max-md:left-4 max-md:w-[calc(100vw-2rem)]"
      :class="style.surfaceClass"
    >
      <div class="flex items-start gap-x-3">
        <div class="flex-1">
          <header v-if="toast.title" class="mb-1 flex flex-1 items-start justify-between">
            <div class="flex flex-1 items-center gap-x-2">
              <LoadingSpinner v-if="isProgress" width="20px" height="20px" class="shrink-0" />
              <Icon v-else :icon="style.icon" class="text-xl" :class="style.iconClass" />
              <h3 class="font-semibold">{{ toast.title }}</h3>
            </div>

            <button aria-label="Dismiss notification" @click="globalStore.dismissToast()">
              <Icon icon="heroicons:x-mark" class="text-base" />
            </button>
          </header>

          <div class="flex items-center gap-x-2">
            <p class="text-xs" :class="style.messageClass">{{ toast.message }}</p>

            <button
              v-if="!toast.title"
              aria-label="Dismiss notification"
              @click="globalStore.dismissToast()"
            >
              <Icon icon="heroicons:x-mark" class="text-base" />
            </button>
          </div>

          <button
            v-if="toast.action"
            class="mt-2 flex items-center gap-1 text-xs font-semibold text-tint-1 transition hover:text-tint-2"
            @click="handleAction"
          >
            <Icon v-if="toast.action.icon" :icon="toast.action.icon" class="text-sm" />
            {{ toast.action.label }}
          </button>
        </div>
      </div>
    </section>
  </Transition>
</template>
